import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * 悬浮提示词编辑组件
 * 
 * 该组件在页面右下角显示一个悬浮按钮，点击后展开编辑区域，
 * 用户可以查看和修改发送给大模型的prompt，并可以收起或发送。
 * 
 * @param {Object} props
 * @param {string} props.updatePrompt - 初始提示词内容
 * @param {function} props.onSendPrompt - 发送提示词的回调函数
 * @param {string} props.buttonText - 悬浮按钮文字，默认为"提示词"
 * @param {string} props.buttonColor - 悬浮按钮颜色，默认为"#F0B90B"（币安黄）
 */
const PromptEditor = ({ 
  updatePrompt = '', 
  onSendPrompt, 
  buttonText = '✨', 
  buttonColor = 'linear-gradient(90deg, #F472B6, #8B5CF6)', 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [prompt, setPrompt] = useState(updatePrompt);
  const textareaRef = useRef(null);

  // 当 updatePrompt 变化时更新编辑器内容
  useEffect(() => {
    setPrompt(updatePrompt);
  }, [updatePrompt]);

  // 切换编辑器的开关状态
  const toggleEditor = () => {
    setIsOpen(!isOpen);
  };

  // 处理提示词内容变化
  const handlePromptChange = (e) => {
    setPrompt(e.target.value);
  };

  // 发送提示词到大模型
  const handleSendPrompt = () => {
    if (onSendPrompt && prompt.trim()) {
      onSendPrompt(prompt);
      // 发送后自动关闭编辑器
      setIsOpen(false);
    }
  };

  return (
    <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end  w-[calc(100%-50px)]">
      

      {/* 编辑区域 */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, height: 0 }}
            animate={{ opacity: 1, y: 0, height: 'auto' }}
            exit={{ opacity: 0, y: 20, height: 0 }}
            transition={{ duration: 0.3 }}
            className="bg-gray-800 rounded-lg shadow-xl mt-3 w-full mx-auto overflow-hidden"
          >
            <div className="p-4">
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-white font-medium">编辑提示词</h3>
                <button 
                  onClick={toggleEditor}
                  className="text-gray-400 hover:text-white focus:outline-none"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
              
              <textarea
                ref={textareaRef}
                value={prompt}
                onChange={handlePromptChange}
                className="w-full bg-gray-700 text-white rounded-md p-1 h-auto min-h-[calc(100vh-300px)] overflow-y-auto focus:outline-none focus:ring-1 focus:ring-yellow-500 resize-none"
                placeholder="输入提示词..."
              />
              
              <div className="flex justify-end mt-3">
                <button
                  onClick={handleSendPrompt}
                  className="px-4 py-2 bg-yellow-500 text-black rounded-md font-medium hover:bg-yellow-400 transition-colors focus:outline-none"
                >
                  发送
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 悬浮按钮 */}
      <button
        onClick={toggleEditor}
        className="flex items-center justify-center w-12 h-12 rounded-full shadow-lg focus:outline-none"
        style={{ background: buttonColor }}
      >
        <span className="text-white font-medium">{buttonText}</span>
      </button>
    </div>
  );
};

export default PromptEditor;
